// src/components/Dashboard/DocumentPreflight.jsx
//
// Resumen previo a generar la LOR o la SPOL: muestra qué datos faltan y deja
// completarlos ahí mismo (sin ir a "Editar reclamo" ni a otro paso). Lo
// escrito se guarda en el reclamo o en los montos del estimado
// (documents/documentFields.js) y después se genera el PDF.
// Nunca bloquea: se puede generar con espacios en blanco, como antes.
import React, { useMemo, useState } from 'react';
import Dialog from '../common/Dialog';
import { buildClaimDocument } from '../../documents/claimDocuments';
import { DOCUMENT_FIELDS, documentFormChanges, initialDocumentForm, isEditableDocumentKey } from '../../documents/documentFields';
import { interestFromPolicyForm } from '../../documents/spolData';
import { updateClaimRecordFields } from '../../services/claimRecordService';
import { saveStepData } from '../../services/flowService';
import './ClaimFlow.css';

// Solo los campos que el resumen sabe editar pueden llegar al reclamo.
const CLAIM_KEYS = Object.values(DOCUMENT_FIELDS).flat().filter(field => field.target !== 'estimate').map(field => field.name);

// Guarda lo completado en el resumen. Devuelve el reclamo y el estimado ya
// actualizados, para generar el PDF con ellos sin volver a leer Firestore.
export const saveDocumentChanges = async (claimRecord, estimate, changes) => {
  const savedClaim = Object.keys(changes.claim).length ? await updateClaimRecordFields(claimRecord.id, changes.claim, CLAIM_KEYS) : {};
  if (Object.keys(changes.estimate).length) await saveStepData('claim', claimRecord.id, 'rs_pol', changes.estimate);
  return { claimChanges: savedClaim, claimRecord: { ...claimRecord, ...savedClaim }, estimate: { ...estimate, ...changes.estimate } };
};

const FieldInput = ({ field, label, value, onChange, es, claimRecord }) => {
  const id = `preflight-${field.name}`;
  const common = { id, className: 'cc-input', value, onChange: e => onChange(field.name, e.target.value) };
  let control;
  if (field.type === 'textarea') control = <textarea rows={3} {...common} />;
  else if (field.type === 'select') {
    control = (
      <select {...common}>
        <option value="">{es ? '— Elegir —' : '— Choose —'}</option>
        {field.options.map(option => <option key={option.value} value={option.value}>{es ? option.label[0] : option.label[1]}</option>)}
      </select>
    );
  } else if (field.type === 'choice') {
    const auto = field.name === 'insuredInterest' ? interestFromPolicyForm(claimRecord.policyForm) : '';
    control = (
      <div className="preflight-choice">
        <div className="preflight-choice-buttons" role="group" aria-label={label}>
          {field.options.map(option => (
            <button key={option} type="button" className={`cd-btn ${value === option ? 'cd-btn-check' : ''}`} aria-pressed={value === option} onClick={() => onChange(field.name, option)}>{option}</button>
          ))}
        </div>
        <input {...common} aria-label={es ? `${label} (otro valor)` : `${label} (other value)`}
          placeholder={auto ? (es ? `Automático: ${auto} (póliza ${claimRecord.policyForm})` : `Automatic: ${auto} (policy ${claimRecord.policyForm})`) : (es ? 'u otro valor' : 'or another value')} />
      </div>
    );
  } else {
    const inputType = field.type === 'number' ? 'text' : (field.type || 'text');
    control = <input type={inputType} inputMode={field.type === 'number' ? 'decimal' : undefined} {...common} />;
  }
  return (
    <div className="cc-input-group">
      {field.type === 'choice' ? <span className="cc-label">{label}</span> : <label className="cc-label" htmlFor={id}>{label}</label>}
      {control}
    </div>
  );
};

const DocumentPreflight = ({ docType, claimRecord, estimate = {}, language = 'es', onClose, onConfirm }) => {
  const es = language === 'es';
  const idx = es ? 0 : 1;
  // Todas las claves del documento, con su etiqueta (un reclamo vacío las marca todas como faltantes).
  const allKeys = useMemo(() => buildClaimDocument(docType, {}, language, {}).missing, [docType, language]);
  // Opcionales (SPOL: hipotecaria y EMS/Vendors): no son faltantes, pero se muestran siempre.
  const optionalItems = useMemo(() => buildClaimDocument(docType, {}, language, {}).optional || [], [docType, language]);
  // Se fija al abrir: un dato que se completa no salta de sección mientras se escribe.
  const [initialMissing] = useState(() => new Set(buildClaimDocument(docType, claimRecord, language, estimate).missing.map(m => m.key)));
  const editableKeys = useMemo(() => [...allKeys, ...optionalItems].map(m => m.key).filter(isEditableDocumentKey), [allKeys, optionalItems]);
  const [form, setForm] = useState(() => initialDocumentForm(editableKeys, claimRecord, estimate));
  const [saving, setSaving] = useState(false);

  const changes = useMemo(() => documentFormChanges(editableKeys, form, claimRecord, estimate), [editableKeys, form, claimRecord, estimate]);
  const dirty = Object.keys(changes.claim).length + Object.keys(changes.estimate).length > 0;
  const stillMissing = useMemo(() => new Set(buildClaimDocument(docType, { ...claimRecord, ...changes.claim }, language, { ...estimate, ...changes.estimate }).missing.map(m => m.key)), [docType, claimRecord, estimate, changes, language]);

  const setField = (name, value) => setForm(current => ({ ...current, [name]: value }));
  const title = `${es ? 'Revisar datos de la' : 'Review data for the'} ${docType.toUpperCase()}`;

  const fieldsFor = item => (DOCUMENT_FIELDS[item.key] || []).map(field => (
    <FieldInput key={field.name} field={field} es={es} claimRecord={claimRecord} value={form[field.name] ?? ''} onChange={setField}
      label={field.label ? field.label[idx] : item.label} />
  ));

  const missingItems = allKeys.filter(m => initialMissing.has(m.key));
  const completeItems = allKeys.filter(m => !initialMissing.has(m.key) && isEditableDocumentKey(m.key));

  const confirm = async () => {
    setSaving(true);
    try {
      await onConfirm(changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog labelledBy="preflight-title" language={language} dirty={dirty && !saving} onClose={onClose} overlayClassName="ap-modal-overlay preflight-overlay" className="preflight-modal">
      {({ requestClose }) => (<>
        <div className="preflight-header">
          <h3 id="preflight-title"><i className="fa-solid fa-list-check" aria-hidden="true"></i> {title}</h3>
          <button type="button" className="ap-modal-close" onClick={requestClose} aria-label={es ? 'Cerrar' : 'Close'}><i className="fa-solid fa-times" aria-hidden="true"></i></button>
        </div>
        <div className="preflight-body">
          <p className="cd-section-hint">{es ? 'Lo que completes aquí se guarda en el reclamo y el documento ya sale lleno.' : 'What you fill in here is saved to the claim and the document comes out filled.'}</p>

          {missingItems.length === 0 ? (
            <p className="doc-complete"><i className="fa-solid fa-circle-check" aria-hidden="true"></i> {es ? 'Todos los datos están completos.' : 'All data is complete.'}</p>
          ) : (
            <section aria-labelledby="preflight-missing">
              <h4 id="preflight-missing" className="preflight-section-title">{es ? `Faltan (${missingItems.length})` : `Missing (${missingItems.length})`}</h4>
              {missingItems.map(item => (
                <div key={item.key} className={`preflight-item ${stillMissing.has(item.key) ? 'is-missing' : 'is-filled'}`}>
                  {isEditableDocumentKey(item.key) ? fieldsFor(item) : (
                    <p className="preflight-readonly"><strong>{item.label}</strong> · {es ? 'complétalo en "Editar reclamo".' : 'fill it in via "Edit claim".'}</p>
                  )}
                  {!stillMissing.has(item.key) && <span className="preflight-ok"><i className="fa-solid fa-check" aria-hidden="true"></i> {es ? 'Listo' : 'Done'}</span>}
                </div>
              ))}
            </section>
          )}

          {optionalItems.length > 0 && (
            <section aria-labelledby="preflight-optional">
              <h4 id="preflight-optional" className="preflight-section-title">{es ? 'Si aplica' : 'If applicable'}</h4>
              <div className="cc-grid">
                {optionalItems.map(item => <React.Fragment key={item.key}>{fieldsFor(item)}</React.Fragment>)}
              </div>
            </section>
          )}

          {completeItems.length > 0 && (
            <details className="preflight-complete">
              <summary>{es ? `Datos completos (${completeItems.length}) · revisar o corregir` : `Complete data (${completeItems.length}) · review or correct`}</summary>
              <div className="cc-grid">
                {completeItems.map(item => <React.Fragment key={item.key}>{fieldsFor(item)}</React.Fragment>)}
              </div>
            </details>
          )}
        </div>
        <div className="ap-modal-footer preflight-footer">
          {stillMissing.size > 0 && <span className="doc-missing">{es ? `${stillMissing.size} dato(s) saldrán en blanco` : `${stillMissing.size} field(s) will be blank`}</span>}
          <button type="button" className="cd-btn" onClick={requestClose} disabled={saving}>{es ? 'Cancelar' : 'Cancel'}</button>
          <button type="button" className="cd-btn cd-btn-check" onClick={confirm} disabled={saving}>
            <i className="fa-solid fa-file-pdf" aria-hidden="true"></i> {saving ? (es ? 'Generando…' : 'Generating…') : dirty ? (es ? 'Guardar y generar' : 'Save and generate') : (es ? 'Generar' : 'Generate')}
          </button>
        </div>
      </>)}
    </Dialog>
  );
};

export default DocumentPreflight;
